import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, Users } from 'lucide-react'
import toast from 'react-hot-toast'

import useRoomStore from '../stores/roomStore'
import AnimatedGradientBackground from '../components/AnimatedGradientBackground'

import FloatingParticles from '../components/FloatingParticles'

const LandingPage = () => {
  const navigate = useNavigate()
  const { createRoom, validateRoom } = useRoomStore()

  const [joinCode, setJoinCode] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [isJoining, setIsJoining] = useState(false)

  const handleCreateRoom = async () => {
    setIsCreating(true)
    try {
      const room = await createRoom()
      navigate(`/tv/${room.code}`)
    } catch (error) {
      console.error('Failed to create room:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleJoinRoom = async (e) => {
    e.preventDefault()

    if (!joinCode.trim()) {
      toast.error('Please enter a room code')
      return
    }

    setIsJoining(true)
    try {
      const validation = await validateRoom(joinCode.toUpperCase())

      if (validation.exists) {
        navigate(`/room/${joinCode.toUpperCase()}`)
      } else {
        toast.error(`Room "${joinCode.toUpperCase()}" does not exist.`)
      }
    } catch (error) {
      console.error('Failed to validate room:', error)
      toast.error('Unable to validate room. Please try again.')
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex flex-col items-center justify-center">
      {/* Background Layers */}
      <AnimatedGradientBackground className="z-0" />
      <FloatingParticles count={40} colors={['#ffffff', '#a78bfa', '#f472b6']} />

      {/* Pulse Effect Behind Card */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl animate-pulse z-0 pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-lg px-6 flex flex-col items-center">
        {/* Animated Hero */}
        <div className="text-center mb-16 space-y-2 fade-in-up pt-[env(safe-area-inset-top)]">
          <h1 className="text-7xl md:text-9xl font-black text-white tracking-tighter mix-blend-overlay opacity-90 drop-shadow-lg scale-in-center">
            LyricSync
          </h1>
        </div>

        {/* Action Stack */}
        <div className="w-full space-y-6">

          {/* Join Room - Primary Action */}
          <div className="group relative transform transition-all hover:scale-105 duration-300">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl blur opacity-40 group-hover:opacity-100 transition duration-500 animate-tilt"></div>
            <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-1 overflow-hidden shadow-2xl">
              <form onSubmit={handleJoinRoom} className="flex flex-col md:flex-row">
                <input
                  type="text"
                  placeholder="ENTER ROOM CODE"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="min-w-0 flex-grow bg-transparent border-none text-white placeholder-white/40 px-6 py-5 text-xl font-bold focus:ring-0 tracking-widest uppercase text-center md:text-left"
                  maxLength={6}
                />
                <button
                  type="submit"
                  disabled={isJoining}
                  className="shrink-0 whitespace-nowrap bg-white/20 hover:bg-white/30 text-white px-8 py-4 md:py-2 rounded-xl font-bold tracking-wide transition-all duration-200 disabled:opacity-50 m-1 shadow-lg border border-white/10"
                >
                  {isJoining ? 'JOINING...' : 'JOIN PARTY'}
                </button>
              </form>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            {/* Host Button */}
            <button
              onClick={handleCreateRoom}
              disabled={isCreating}
              className="group relative flex flex-col items-center justify-center p-8 rounded-3xl bg-white/25 hover:bg-white/40 border border-white/30 backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="bg-white/10 p-3 rounded-full mb-3 group-hover:bg-blue-500/20 group-hover:scale-110 transition-all duration-300">
                <Users className="w-8 h-8 text-white/90 group-hover:text-blue-300" />
              </div>
              <span className="text-white/90 font-bold tracking-wide text-lg">HOST</span>
              {isCreating && <div className="absolute inset-0 bg-black/20 rounded-3xl flex items-center justify-center"><div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></div></div>}
            </button>

            {/* Practice Button */}
            <button
              onClick={() => navigate('/practice')}
              className="group relative flex flex-col items-center justify-center p-8 rounded-3xl bg-white/25 hover:bg-white/40 border border-white/30 backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="bg-white/10 p-3 rounded-full mb-3 group-hover:bg-purple-500/20 group-hover:scale-110 transition-all duration-300">
                <Mic className="w-8 h-8 text-white/90 group-hover:text-purple-300" />
              </div>
              <span className="text-white/90 font-bold tracking-wide text-lg">SOLO</span>
            </button>
          </div>

        </div>

      </div>
    </div>
  )
}

export default LandingPage
